/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { useLogger } from '@airasia-rn/core-logger';
import { useUser } from './account';
import { isProfileComplete } from '../utils/signUpFormUtils';
import {
  logSsoAuthSuccess,
  resolveSsoAuthMethod,
} from '../utils/loginSignupAnalyticsUtils';

/**
 * Logs `aa_sso_login_successful` for the login sheets, which don't use
 * useFinishSignIn. Only logs for existing users (complete profile).
 */
export function useLogSheetLoginSuccess(caller: string) {
  const { refetch } = useUser(caller);
  const { logEvent } = useLogger();

  return useCallback(
    async (method?: string) => {
      // Analytics must never break login, so errors are ignored.
      try {
        const user = (await refetch()).data;
        if (!isProfileComplete(user)) return;
        logSsoAuthSuccess(
          logEvent,
          false,
          resolveSsoAuthMethod(method),
          user?.id,
        );
      } catch {
        // Nothing to do: the event is just not logged.
      }
    },
    [logEvent, refetch],
  );
}
